import { AllRolePermissions } from "@/Contracts/Users";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import { EDocumentMenuItems } from "@/Contracts/Documents";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";

interface ModulePermissions {
	[ModulePermission.READ]: AllRolePermissions[];
	[ModulePermission.CREATE_EDIT_DELETE_OWNED]: AllRolePermissions[];
	[ModulePermission.EDIT_ALL]: AllRolePermissions[];
}

interface Mapper {
	[key: string]: ModulePermissions;
}

/**
 *
 * @param module: Required. To get permissions based on the module, the module name from ModuleEntities should be passed.
 *
 * @param modulePermission: Required. Base permission on which other permissions of the same module depend on.
 *
 * Example: **OPPORTUNITY_IMPORT** depends on **READ & CREDIT_EDIT_DELETE_OWNED** combination.
 *
 * @param extraPermissions?: Optional. Extra permissions as per the requirement can be passed.
 *
 * @description computes the permission array for a module inorder to view the feature.
 *
 * @returns Array of permissions.
 *
 * @author Idrees
 */
const usePermissions = (
	module: Modules,
	modulePermission: ModulePermission,
	extraPermissions?: AllRolePermissions[]
) => {
	const READ = `${module}_READ` as keyof typeof AllRolePermissions;
	const CREATE = `${module}_CREATE` as keyof typeof AllRolePermissions;
	const EDIT_ALL = `${module}_EDIT_ALL` as keyof typeof AllRolePermissions;

	const mapper: Mapper = {
		[module]: {
			[ModulePermission.READ]: [AllRolePermissions[READ]],
			[ModulePermission.CREATE_EDIT_DELETE_OWNED]: [
				AllRolePermissions[READ],
				AllRolePermissions[CREATE]
			],
			[ModulePermission.EDIT_ALL]: [
				AllRolePermissions[READ],
				AllRolePermissions[CREATE],
				AllRolePermissions[EDIT_ALL]
			]
		}
	};

	if (!!extraPermissions?.length)
		return [...mapper[module][modulePermission], ...extraPermissions];
	return mapper[module][modulePermission];
};

export const useDocumentEntityFeatureAccess = (
	actionType: string,
	isCurrentUserOwner: boolean
): Record<string, boolean> => {
	const rolePermissions = useSelector(
		(state: GlobalState) =>
			state.user.userData?.roles_permissions?.permissions
	);

	const allDocumentTypes = [
		"proposal",
		"contract",
		"quote",
		"sales_presentation",
		"assessment",
		"qbr",
		"specific_order_form",
		"generic_order_form"
	];

	const actionToPermissionMap: Record<string, AllRolePermissions> = {
		[EDocumentMenuItems.SEND]: AllRolePermissions.DOCUMENT_SEND,
		[EDocumentMenuItems.SHAREVIALINK]: AllRolePermissions.DOCUMENT_SHARE,
		[EDocumentMenuItems.EDITDETAILS]: AllRolePermissions.DOCUMENT_EDIT_ALL,
		[EDocumentMenuItems.DELETE]: AllRolePermissions.DOCUMENT_DELETE_ALL,
		[EDocumentMenuItems.CLONE]: AllRolePermissions.DOCUMENT_CREATE
	};

	const requiredPermission = actionToPermissionMap[actionType];
	const hasRequiredPermission =
		requiredPermission && rolePermissions?.includes(requiredPermission);

	const accessMap: Record<string, boolean> = {};

	allDocumentTypes.forEach(documentType => {
		if (isCurrentUserOwner || hasRequiredPermission) {
			accessMap[documentType] = true;
		} else {
			accessMap[documentType] = false;
		}
	});

	return accessMap;
};

export const useDocumentEntityPermissions = useDocumentEntityFeatureAccess;

export const useDocumentFeatureAccess = (): Record<
	string,
	{ READ: boolean; EDIT: boolean; EDITALL: boolean }
> => {
	const allDocumentTypes = [
		"proposal",
		"contract",
		"quote",
		"sales_presentation",
		"assessment",
		"qbr",
		"specific_order_form",
		"generic_order_form"
	];

	const permissionMap: Record<
		string,
		{ READ: boolean; EDIT: boolean; EDITALL: boolean }
	> = {};

	allDocumentTypes.forEach(documentType => {
		permissionMap[documentType] = {
			READ: true,
			EDIT: true,
			EDITALL: true
		};
	});

	return permissionMap;
};

export default usePermissions;
